import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import AdminDashboardLayout from "../../layouts/AdminDashboardLayout";
import BrandLoader from "../../components/BrandLoader";
import { adminApi } from "../../services/adminApi";
import { notificationAPI } from "../../services/api";
import {
  FiAlertTriangle, FiUserX, FiCreditCard, FiFeather,
  FiUserPlus, FiHeart, FiSearch, FiBell, FiCheckCircle,
  FiX, FiChevronDown, FiChevronUp, FiEye, FiDownload,
  FiTrendingUp, FiRefreshCw, FiArrowRight, FiCalendar,
  FiZap, FiCheck, FiSend, FiUserCheck, FiPhone, FiMail,
  FiActivity, FiFileText, FiAward, FiTag, FiClock, FiAlertCircle
} from "react-icons/fi";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  olive: "#6B8A5A", olive2: "#8BA47A",
  orange: "#F29A0C", orange2: "#E97818",
  dark: "#1F1F1F", mid: "#555", muted: "#888", light: "#CCC",
  border: "rgba(239,229,216,0.8)",
  green: "#22C55E", greenDk: "#16A34A",
  red: "#E24B4A", amber: "#F59E0B", blue: "#185FA5", purple: "#534AB7",
};

const glass = {
  background: "rgba(255,255,255,0.88)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 16px 48px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

// ─── SEGMENT CONFIGURATION ────────────────────────────────────────────────────
const SEGMENTS = [
  {
    id: "expiring",
    label: "Expiring Subscriptions",
    sublabel: "Ending within 7 days",
    icon: FiCalendar,
    color: T.red,
    bg: "rgba(226,75,74,0.08)",
    border: "rgba(226,75,74,0.2)",
    urgency: "critical",
    action: "Extend",
    actionIcon: FiRefreshCw,
    defaultNotif: "Your subscription is expiring soon. Renew now or extend to maintain uninterrupted yoga classes!"
  },
  {
    id: "inactive",
    label: "Inactive Users",
    sublabel: "No login in 14+ days",
    icon: FiUserX,
    color: T.amber,
    bg: "rgba(245,158,11,0.08)",
    border: "rgba(245,158,11,0.2)",
    urgency: "high",
    action: "Nudge",
    actionIcon: FiBell,
    defaultNotif: "We miss you on the mat! Join our daily live yoga and meditation sessions this week."
  },
  {
    id: "failed",
    label: "Failed & Pending Cash Payments",
    sublabel: "Awaiting activation or payment retry",
    icon: FiCreditCard,
    color: T.red,
    bg: "rgba(226,75,74,0.08)",
    border: "rgba(226,75,74,0.2)",
    urgency: "critical",
    action: "Activate Cash",
    actionIcon: FiCheckCircle,
    defaultNotif: "Your subscription payment is pending confirmation. Our team has updated your account status."
  },
  {
    id: "completion",
    label: "Near Program End",
    sublabel: "90%+ program completion",
    icon: FiFeather,
    color: T.olive,
    bg: "rgba(107,138,90,0.08)",
    border: "rgba(107,138,90,0.2)",
    urgency: "medium",
    action: "Upgrade",
    actionIcon: FiArrowRight,
    defaultNotif: "Congratulations on completing your program milestone! Claim your upgrade to Premium with special benefits."
  },
  {
    id: "new",
    label: "New Registrations",
    sublabel: "Joined recently · onboarding follow-up",
    icon: FiUserPlus,
    color: T.blue,
    bg: "rgba(24,95,165,0.08)",
    border: "rgba(24,95,165,0.2)",
    urgency: "medium",
    action: "Onboard",
    actionIcon: FiZap,
    defaultNotif: "Welcome to Divyyog! Explore our beginner live classes and personalized diet plans today."
  },
  {
    id: "health",
    label: "Health Flagged",
    sublabel: "Instructor review required",
    icon: FiHeart,
    color: T.purple,
    bg: "rgba(83,74,183,0.08)",
    border: "rgba(83,74,183,0.2)",
    urgency: "high",
    action: "Health Review",
    actionIcon: FiEye,
    defaultNotif: "Our yoga instructors have reviewed your health preferences. Check your modified practice guidelines!"
  },
];

const TIER_STYLES = {
  Free:         { bg: "rgba(200,200,200,0.15)", c: "#555",    b: "rgba(200,200,200,0.3)" },
  "Free Trial": { bg: "rgba(200,200,200,0.15)", c: "#555",    b: "rgba(200,200,200,0.3)" },
  Basic:        { bg: "rgba(24,95,165,0.12)",  c: "#0C447C", b: "rgba(24,95,165,0.25)" },
  "Basic Plan": { bg: "rgba(24,95,165,0.12)",  c: "#0C447C", b: "rgba(24,95,165,0.25)" },
  Standard:     { bg: "rgba(83,74,183,0.12)",  c: "#3C3489", b: "rgba(83,74,183,0.25)" },
  "Standard Plan": { bg: "rgba(83,74,183,0.12)", c: "#3C3489", b: "rgba(83,74,183,0.25)" },
  Premium:      { bg: "rgba(242,154,12,0.15)", c: "#C8660A", b: "rgba(242,154,12,0.3)" },
  "Premium Plan": { bg: "rgba(242,154,12,0.15)", c: "#C8660A", b: "rgba(242,154,12,0.3)" },
  Pro:          { bg: "rgba(107,138,90,0.15)", c: "#27500A", b: "rgba(107,138,90,0.3)" },
  "YTT Pro":    { bg: "rgba(107,138,90,0.15)", c: "#27500A", b: "rgba(107,138,90,0.3)" },
  YTT:          { bg: "rgba(107,138,90,0.15)", c: "#27500A", b: "rgba(107,138,90,0.3)" },
};

const AVGRADS = [
  "linear-gradient(135deg,#F29A0C,#E97818)",
  "linear-gradient(135deg,#6B8A5A,#8BA47A)",
  "linear-gradient(135deg,#534AB7,#7F77DD)",
  "linear-gradient(135deg,#185FA5,#378ADD)",
  "linear-gradient(135deg,#E24B4A,#F87171)",
  "linear-gradient(135deg,#0F6E56,#1D9E75)",
];

// Helper to format health condition tags cleanly
function parseHealthTags(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(c => c && c.toLowerCase() !== "none");
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter(c => c && c.toLowerCase() !== "none");
  } catch (e) {}
  const clean = String(raw).replace(/[\[\]\"']/g, "").trim();
  if (!clean || clean.toLowerCase() === "none") return [];
  return clean.split(",").map(s => s.trim()).filter(s => s && s.toLowerCase() !== "none");
}

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────
const PriorityBadge = ({ score }) => {
  const num = Number(score) || 0;
  const col = num >= 90 ? T.red : num >= 75 ? T.amber : T.olive;
  return (
    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-black"
      style={{ background: `${col}12`, borderColor: `${col}30`, color: col }}>
      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: col }} />
      <span>{num}</span>
    </div>
  );
};

const TierPill = ({ tier }) => {
  const normalizeTier = (val) => {
    if (!val) return "Free Trial";
    const s = String(val).toLowerCase().trim();
    if (s.includes("free") || s.includes("trial")) return "Free Trial";
    if (s.includes("ytt") || s.includes("pro")) return "YTT Pro";
    if (s.includes("prem")) return "Premium";
    if (s.includes("stand")) return "Standard";
    if (s.includes("basic")) return "Basic";
    return val;
  };

  const canonical = normalizeTier(tier);
  const s = TIER_STYLES[canonical] || TIER_STYLES[tier] || TIER_STYLES["Free Trial"];
  const displayLabel = tier && tier !== "Free" ? tier : "Free Trial";

  return (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border tracking-wide uppercase"
      style={{ background: s.bg, color: s.c, borderColor: s.b }}>
      {displayLabel}
    </span>
  );
};

// ─── MODAL: EXTEND SUBSCRIPTION ───────────────────────────────────────────────
const ExtendModal = ({ user, onClose, onSuccess, showToast }) => {
  const [days, setDays] = useState(30);
  const [customDays, setCustomDays] = useState("");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validateCustomDays = (val) => {
    if (!val || val.trim() === "") return "";
    const num = Number(val);
    if (isNaN(num) || !/^[1-9]\d*$/.test(val.trim())) {
      return "Extension duration must be a whole positive number of days.";
    }
    if (num < 1) {
      return "Extension duration must be at least 1 day.";
    }
    if (num > 365) {
      return "Extension duration cannot exceed 365 days (1 year).";
    }
    return "";
  };

  const handleCustomDaysChange = (val) => {
    setCustomDays(val);
    if (touched) {
      setError(validateCustomDays(val));
    }
  };

  const handleCustomDaysBlur = () => {
    setTouched(true);
    setError(validateCustomDays(customDays));
  };

  const selectPreset = (d) => {
    setDays(d);
    setCustomDays("");
    setError("");
    setTouched(false);
  };

  const effectiveDays = useMemo(() => {
    if (customDays && customDays.trim() !== "") {
      const parsed = Number(customDays);
      return !isNaN(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
    }
    return days;
  }, [customDays, days]);

  const newExpiryDateStr = useMemo(() => {
    if (effectiveDays <= 0) return null;
    const baseDate = user.endDate ? new Date(user.endDate) : new Date();
    const effectiveBase = isNaN(baseDate.getTime()) || baseDate < new Date() ? new Date() : baseDate;
    const targetDate = new Date(effectiveBase.getTime() + (effectiveDays * 24 * 60 * 60 * 1000));
    return targetDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  }, [effectiveDays, user.endDate]);

  const isValid = !error && effectiveDays > 0;

  const handleExtend = async () => {
    setTouched(true);
    const err = validateCustomDays(customDays);
    if (err) {
      setError(err);
      return;
    }
    if (effectiveDays <= 0) {
      setError("Please select a valid extension duration.");
      return;
    }

    setSubmitting(true);
    try {
      await adminApi.extendSubscription({
        subscriptionId: user.subscriptionId && user.subscriptionId > 0 ? Number(user.subscriptionId) : null,
        userId: Number(user.id),
        days: effectiveDays
      });

      window.dispatchEvent(new Event("subscription-updated"));

      await notificationAPI.sendNotification({
        title: "Subscription Extended",
        message: `Your Divyyog subscription has been extended by ${effectiveDays} days. Enjoy your yoga practice!`,
        audience: user.email || `User ID: ${user.id}`
      }).catch(() => null);

      showToast(`✓ Added ${effectiveDays} days to ${user.name}'s plan!`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      showToast(err.message || "Failed to extend subscription", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(15,15,15,0.45)", backdropFilter: "blur(8px)" }} onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="relative rounded-[28px] p-6 max-w-[460px] w-full"
        style={{ background: "#FFFFFF", boxShadow: "0 30px 90px rgba(0,0,0,0.22)", border: "1px solid rgba(255,255,255,0.9)" }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white bg-emerald-600 shadow-md">
              <FiCalendar size={18} />
            </div>
            <div>
              <h3 className="font-black text-[16px] text-zinc-900 tracking-tight">Extend Subscription</h3>
              <p className="text-[11px] text-zinc-500 font-medium">Extend active membership for <strong>{user.name}</strong></p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 hover:text-zinc-900 border-none cursor-pointer">
            <FiX size={15} />
          </button>
        </div>

        <div className="space-y-4 my-4">
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-zinc-500 block mb-2">Select Extension Preset</label>
            <div className="grid grid-cols-4 gap-2">
              {[7, 15, 30, 60].map(d => (
                <button key={d} type="button" onClick={() => selectPreset(d)}
                  className={`py-2 rounded-xl text-[12px] font-black border transition-all cursor-pointer ${
                    days === d && !customDays ? "bg-emerald-600 text-white border-emerald-600 shadow-md" : "bg-zinc-50 text-zinc-700 border-zinc-200 hover:bg-zinc-100"
                  }`}>
                  +{d} Days
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Or Enter Custom Days</label>
              {customDays && !error && (
                <span className="text-[10px] text-emerald-600 font-extrabold flex items-center gap-1">
                  <FiCheck size={11} /> Custom Preset Active
                </span>
              )}
            </div>
            <div className="relative">
              <input
                type="number"
                min="1"
                max="365"
                step="1"
                value={customDays}
                onChange={e => handleCustomDaysChange(e.target.value)}
                onBlur={handleCustomDaysBlur}
                placeholder="e.g. 90 (1 to 365 days)"
                className={`w-full rounded-xl px-3.5 py-2.5 text-[13px] font-bold border outline-none transition-all ${
                  error
                    ? "border-red-500 bg-red-50/20 text-red-900 focus:border-red-600"
                    : customDays && !error
                    ? "border-emerald-500 bg-emerald-50/20 text-zinc-900 focus:border-emerald-600"
                    : "border-zinc-200 focus:border-amber-500"
                }`}
              />
              {customDays && !error && (
                <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={15} />
              )}
            </div>
            {error ? (
              <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                <FiAlertTriangle size={11} className="shrink-0" /> {error}
              </p>
            ) : (
              <p className="text-[10px] text-zinc-400 mt-1 font-medium">Enter whole integer between 1 and 365 days</p>
            )}
          </div>

          {/* Dynamic Expiry Calculation Banner */}
          {newExpiryDateStr && !error && (
            <div className="rounded-[16px] p-3.5 bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between">
              <div>
                <span className="text-[11px] font-bold text-emerald-950 block">Calculated Expiration Date</span>
                <span className="text-[10px] text-emerald-700">Updated membership end date after extension</span>
              </div>
              <div className="text-right">
                <span className="text-[10px] font-bold text-emerald-600 block uppercase">+{effectiveDays} Days Added</span>
                <span className="text-[14px] font-black text-emerald-800">{newExpiryDateStr}</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2.5 mt-6">
          <button disabled={submitting || !isValid} onClick={handleExtend}
            className="flex-1 py-3 rounded-full text-white text-[13px] font-extrabold bg-emerald-600 hover:bg-emerald-700 border-none cursor-pointer shadow-lg disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting ? "Extending..." : `Confirm +${effectiveDays} Days`}
          </button>
          <button onClick={onClose} className="px-5 py-3 rounded-full text-[13px] font-bold bg-zinc-100 text-zinc-700 border border-zinc-200 cursor-pointer">
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── MODAL: SEND PUSH NOTIFICATION ────────────────────────────────────────────
const SendNotificationModal = ({ target, onClose, showToast }) => {
  const isBulk = target.type === "segment";
  const [title, setTitle] = useState(
    isBulk 
      ? `Priority Update: ${target.seg.label}` 
      : `Important Update for ${target.user.name}`
  );
  const [message, setMessage] = useState(
    isBulk 
      ? target.seg.defaultNotif 
      : `Hello ${target.user.name}, our yoga instructors have an important update regarding your account.`
  );
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validateField = (field, val) => {
    const v = typeof val === "string" ? val.trim() : val;
    if (field === "title") {
      if (!v) return "Notification title is required.";
      if (v.length < 3) return "Title must be at least 3 characters.";
      if (v.length > 100) return "Title cannot exceed 100 characters.";
      if (/<[^>]*>/g.test(v)) return "Title contains invalid characters or HTML tags.";
    }
    if (field === "message") {
      if (!v) return "Message content is required.";
      if (v.length < 5) return "Message content must be at least 5 characters.";
      if (v.length > 500) return "Message content cannot exceed 500 characters.";
    }
    return "";
  };

  const handleChange = (field, val) => {
    if (field === "title") setTitle(val);
    if (field === "message") setMessage(val);

    if (touched[field]) {
      const err = validateField(field, val);
      setErrors(prev => ({ ...prev, [field]: err }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const val = field === "title" ? title : message;
    const err = validateField(field, val);
    setErrors(prev => ({ ...prev, [field]: err }));
  };

  const isValid = !errors.title && !errors.message && title.trim().length >= 3 && message.trim().length >= 5;

  const handleSend = async () => {
    const titleErr = validateField("title", title);
    const msgErr = validateField("message", message);
    setTouched({ title: true, message: true });
    setErrors({ title: titleErr, message: msgErr });

    if (titleErr || msgErr) return;

    setSubmitting(true);
    try {
      const audience = isBulk 
        ? (target.seg.id === "expiring" ? "Expiring Soon" :
           target.seg.id === "inactive" ? "Inactive 14d+" :
           target.seg.id === "failed" ? "Failed Payments" :
           target.seg.id === "completion" ? "Near Program End" :
           target.seg.id === "new" ? "New Registrations" : "Health Flagged")
        : (target.user.email || `User ID: ${target.user.id}`);

      await notificationAPI.sendNotification({
        title: title.trim(),
        message: message.trim(),
        audience
      });

      showToast(`✓ Push notification sent to ${isBulk ? target.seg.label : target.user.name}!`);
      onClose();
    } catch (err) {
      showToast(err.message || "Failed to send notification", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(15,15,15,0.45)", backdropFilter: "blur(8px)" }} onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="relative rounded-[28px] p-6 max-w-[500px] w-full"
        style={{ background: "#FFFFFF", boxShadow: "0 30px 90px rgba(0,0,0,0.22)", border: "1px solid rgba(255,255,255,0.9)" }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white bg-amber-500 shadow-md">
              <FiBell size={18} />
            </div>
            <div>
              <h3 className="font-black text-[16px] text-zinc-900 tracking-tight">
                {isBulk ? `Notify All: ${target.seg.label}` : `Send Push to ${target.user.name}`}
              </h3>
              <p className="text-[11px] text-zinc-500 font-medium">Sends instant in-app and browser Web Push alerts</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 border-none cursor-pointer">
            <FiX size={15} />
          </button>
        </div>

        <div className="space-y-4 my-4">
          {/* Title Field */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-[10px] font-black uppercase tracking-wider text-zinc-500">
                Notification Title <span className="text-red-500">*</span>
              </label>
              <span className={`text-[10px] font-bold ${title.length > 100 ? "text-red-500" : "text-zinc-400"}`}>
                {title.length}/100
              </span>
            </div>
            <div className="relative">
              <input
                type="text"
                maxLength={100}
                value={title}
                onChange={e => handleChange("title", e.target.value)}
                onBlur={() => handleBlur("title")}
                placeholder="e.g. Special Yoga Workshop Update"
                className={`w-full rounded-xl px-3.5 py-2.5 text-[13px] font-bold border outline-none transition-all ${
                  touched.title && errors.title
                    ? "border-red-500 bg-red-50/20 text-red-900 focus:border-red-600"
                    : touched.title && !errors.title && title.trim()
                    ? "border-emerald-500 bg-emerald-50/20 text-zinc-900 focus:border-emerald-600"
                    : "border-zinc-200 focus:border-amber-500"
                }`}
              />
              {touched.title && !errors.title && title.trim() && (
                <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={15} />
              )}
            </div>
            {touched.title && errors.title && (
              <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                <FiAlertTriangle size={11} className="shrink-0" /> {errors.title}
              </p>
            )}
          </div>

          {/* Message Textarea */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-[10px] font-black uppercase tracking-wider text-zinc-500">
                Message Content <span className="text-red-500">*</span>
              </label>
              <span className={`text-[10px] font-bold ${message.length > 500 ? "text-red-500" : "text-zinc-400"}`}>
                {message.length}/500
              </span>
            </div>
            <div className="relative">
              <textarea
                rows={3}
                maxLength={500}
                value={message}
                onChange={e => handleChange("message", e.target.value)}
                onBlur={() => handleBlur("message")}
                placeholder="Write clear, engaging notification message for the user..."
                className={`w-full rounded-xl px-3.5 py-2.5 text-[12px] font-medium border outline-none transition-all resize-none leading-relaxed ${
                  touched.message && errors.message
                    ? "border-red-500 bg-red-50/20 text-red-900 focus:border-red-600"
                    : touched.message && !errors.message && message.trim()
                    ? "border-emerald-500 bg-emerald-50/20 text-zinc-900 focus:border-emerald-600"
                    : "border-zinc-200 focus:border-amber-500"
                }`}
              />
              {touched.message && !errors.message && message.trim() && (
                <FiCheck className="absolute right-3.5 top-3 text-emerald-500" size={15} />
              )}
            </div>
            {touched.message && errors.message && (
              <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                <FiAlertTriangle size={11} className="shrink-0" /> {errors.message}
              </p>
            )}
          </div>

          {/* Live Web Push Mobile Notification Card Preview */}
          <div className="rounded-[18px] p-3.5 bg-zinc-900 text-white border border-zinc-800 shadow-inner">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-zinc-800">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-amber-500 flex items-center justify-center text-white text-[10px]">
                  <FiBell size={11} />
                </div>
                <span className="text-[11px] font-bold text-zinc-300">Divyyog · In-App & Web Push</span>
              </div>
              <span className="text-[9px] font-bold uppercase tracking-wider text-amber-400">Live Preview</span>
            </div>
            <p className="font-extrabold text-[13px] text-amber-400 truncate mb-1">
              {title.trim() || "Notification Title"}
            </p>
            <p className="text-[11px] text-zinc-300 leading-snug line-clamp-2">
              {message.trim() || "Notification message will appear here..."}
            </p>
            <div className="mt-2 pt-2 border-t border-zinc-800/80 flex justify-between items-center text-[10px] text-zinc-400">
              <span>Audience: <strong className="text-zinc-200">{isBulk ? target.seg.label : target.user.name}</strong></span>
              <span>Instant Dispatch</span>
            </div>
          </div>
        </div>

        <div className="flex gap-2.5 mt-6">
          <button disabled={submitting || !isValid} onClick={handleSend}
            className="flex-1 py-3 rounded-full text-white text-[13px] font-extrabold bg-amber-500 hover:bg-amber-600 border-none cursor-pointer shadow-lg flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            <FiSend size={14} /> {submitting ? "Sending Push..." : "Send Notification"}
          </button>
          <button onClick={onClose} className="px-5 py-3 rounded-full text-[13px] font-bold bg-zinc-100 text-zinc-700 border border-zinc-200 cursor-pointer">
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── USER ROW COMPONENT ───────────────────────────────────────────────────────
// ─── USER ROW COMPONENT ───────────────────────────────────────────────────────
const UserRow = ({ user, seg, onView, onAction, onNotify, delay = 0 }) => {
  const healthTags = useMemo(() => parseHealthTags(user.healthConditions), [user.healthConditions]);
  const [imgError, setImgError] = useState(false);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.25 }}
      className="ad-row group relative flex items-center gap-3 p-3 rounded-[16px] border transition-all duration-200 hover:shadow-md"
      style={{
        background: "rgba(255,255,255,0.92)",
        borderColor: "rgba(239,229,216,0.6)",
      }}>
      
      {/* Priority score pill */}
      <PriorityBadge score={user.priorityScore} />

      {/* Avatar / Profile Image */}
      <div className="relative flex-shrink-0">
        {user.profileImageUrl && !imgError ? (
          <img
            src={user.profileImageUrl}
            alt={user.name}
            onError={() => setImgError(true)}
            className="w-10 h-10 rounded-[13px] object-cover shadow-sm border border-zinc-200"
          />
        ) : (
          <div className="w-10 h-10 rounded-[13px] flex items-center justify-center text-white text-[12px] font-black overflow-hidden shadow-sm"
            style={{ background: user.grad || AVGRADS[0], fontFamily: "'Poppins',sans-serif", position: "relative" }}>
            <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,rgba(255,255,255,0.22),transparent)" }} />
            <span className="relative z-10">{user.initials}</span>
          </div>
        )}
      </div>

      {/* User Info */}
      <div className="ad-row-main flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[13px] font-extrabold text-[#1F1F1F] truncate">{user.name}</p>
          <TierPill tier={user.tier} />
        </div>
        <p className="text-[11px] truncate mt-0.5" style={{ color: T.muted }}>
          {user.email} {user.phone ? `· ${user.phone}` : ""}
        </p>

        {/* Clean Health tags or goal pills */}
        {seg.id === "health" && healthTags.length > 0 && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {healthTags.slice(0, 2).map((h, idx) => (
              <span key={idx} className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">
                ⚠️ {h}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Dynamic Urgency / Status Badge */}
      <div className="ad-row-meta flex-shrink-0 text-right">
        <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full inline-block"
          style={{ background: `${user.urgencyColor || seg.color}14`, color: user.urgencyColor || seg.color, border: `1px solid ${user.urgencyColor || seg.color}28` }}>
          {user.label || `${user.daysLeft ?? 7}d left`}
        </span>
        <p className="text-[10px] text-zinc-400 mt-0.5">{user.lastSeen || "Active recently"}</p>
      </div>

      {/* Actions */}
      <div className="ad-row-actions flex items-center gap-1.5 flex-shrink-0">
        {/* Primary Segment Action Button */}
        <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.95 }}
          onClick={() => onAction(user, seg)}
          className="h-8 px-3 rounded-full text-white text-[11px] font-extrabold flex items-center gap-1.5 border-none cursor-pointer shadow-sm transition-all"
          style={{
            background: `linear-gradient(135deg, ${seg.color === "#E24B4A" ? T.orange : seg.color}, ${seg.color === "#E24B4A" ? T.orange2 : seg.color + "DD"})`,
            fontFamily: "'Inter',sans-serif"
          }}>
          <seg.actionIcon size={12} />
          <span>{seg.action}</span>
        </motion.button>

        {/* 1-Click Push Notify Button — only shown if primary action is NOT already a notification */}
        {seg.id !== "inactive" && seg.id !== "new" && (
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={() => onNotify(user, seg)}
            title="Send Targeted Notification"
            className="w-8 h-8 rounded-[11px] flex items-center justify-center border cursor-pointer bg-white text-zinc-600 hover:text-amber-600 hover:border-amber-300 transition-all shadow-sm">
            <FiBell size={13} />
          </motion.button>
        )}

        {/* View Profile Drawer Button — only shown if primary action is NOT already viewing profile */}
        {seg.id !== "health" && (
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={() => onView(user)}
            title="View Full Profile Details"
            className="w-8 h-8 rounded-[11px] flex items-center justify-center border cursor-pointer bg-white text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 transition-all shadow-sm">
            <FiEye size={13} />
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

// ─── ACCORDION SEGMENT PANEL ──────────────────────────────────────────────────
const SegmentPanel = ({ seg, users, onView, onAction, onNotify, onNotifyAll, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen || seg.urgency === "critical");
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? users : users.slice(0, 5);

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-[24px] overflow-hidden border mb-4 transition-all duration-300"
      style={{
        background: "rgba(255,255,255,0.85)",
        borderColor: seg.urgency === "critical" ? `${seg.color}35` : "rgba(255,255,255,0.95)",
        backdropFilter: "blur(32px)",
        boxShadow: seg.urgency === "critical" ? "0 16px 48px rgba(226,75,74,0.06)" : "0 16px 48px rgba(0,0,0,0.04)"
      }}>

      {/* Header Bar */}
      <div className="ad-wrap w-full flex items-center justify-between gap-3 px-5 py-4 cursor-pointer select-none"
        style={{ background: open ? `${seg.bg}` : "transparent" }}
        onClick={() => setOpen(o => !o)}>
        
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="w-10 h-10 rounded-[14px] flex items-center justify-center flex-shrink-0 shadow-sm"
            style={{ background: seg.bg, border: `1px solid ${seg.border}` }}>
            <seg.icon size={18} style={{ color: seg.color }} />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-black text-[15px] text-[#1F1F1F] tracking-tight"
                style={{ fontFamily: "'Poppins',sans-serif" }}>
                {seg.label}
              </h3>
              {seg.urgency === "critical" && (
                <span className="text-[9px] font-black uppercase tracking-[1px] px-2 py-0.5 rounded-full"
                  style={{ background: `${seg.color}18`, color: seg.color, border: `1px solid ${seg.color}30` }}>
                  CRITICAL
                </span>
              )}
            </div>
            <p className="text-[12px] text-zinc-500 truncate mt-0.5">{seg.sublabel}</p>
          </div>
        </div>

        {/* Count Badge + Notify All + Toggle */}
        <div className="ad-full ad-end flex items-center gap-2.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-center px-2.5 h-7 rounded-full font-black text-[13px] border"
            style={{ background: `${seg.color}15`, color: seg.color, borderColor: `${seg.color}30`, fontFamily: "'Poppins',sans-serif" }}>
            {users.length} {users.length === 1 ? "user" : "users"}
          </div>

          {/* Notify All Button */}
          {users.length > 0 && (
            <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              onClick={() => onNotifyAll(seg)}
              className="h-7 px-3 rounded-full text-white text-[10px] font-extrabold flex items-center gap-1.5 border-none cursor-pointer shadow-sm"
              style={{
                background: `linear-gradient(135deg, ${seg.color}, ${seg.color}DD)`,
                boxShadow: `0 3px 10px ${seg.color}25`
              }}>
              <FiBell size={11} /> Notify All ({users.length})
            </motion.button>
          )}

          <button onClick={() => setOpen(o => !o)}
            className="w-8 h-8 rounded-full flex items-center justify-center bg-white border border-zinc-200 text-zinc-500 hover:text-zinc-900 transition-all cursor-pointer">
            <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }}>
              <FiChevronDown size={16} />
            </motion.div>
          </button>
        </div>
      </div>

      {/* Accordion List Body */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}>
            <div className="px-5 pb-5 pt-2 flex flex-col gap-2 border-t" style={{ borderColor: "rgba(239,229,216,0.6)" }}>
              {users.length === 0 ? (
                <div className="py-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-2">
                    <FiCheckCircle size={22} />
                  </div>
                  <p className="text-[13px] font-bold text-zinc-800">All clear! No users in this alert tier.</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Checks show no required admin actions right now.</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-2 pt-2">
                    {visible.map((user, i) => (
                      <UserRow key={user.id || i} user={user} seg={seg}
                        onView={onView} onAction={onAction} onNotify={onNotify}
                        delay={i * 0.03} />
                    ))}
                  </div>

                  {users.length > 5 && (
                    <motion.button whileHover={{ background: "#fff" }}
                      onClick={() => setShowAll(s => !s)}
                      className="w-full h-9 rounded-[14px] text-[12px] font-bold flex items-center justify-center gap-2 mt-2 bg-white/90 border border-zinc-200 text-zinc-700 cursor-pointer shadow-sm">
                      {showAll ? (
                        <><FiChevronUp size={14} /> Collapse List</>
                      ) : (
                        <><FiChevronDown size={14} /> View All {users.length} Users</>
                      )}
                    </motion.button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── USER DETAIL DRAWER ───────────────────────────────────────────────────────
const UserDrawer = ({ user, onClose, onAction, onNotify, showToast }) => {
  const [imgError, setImgError] = useState(false);
  if (!user) return null;
  const seg = SEGMENTS.find(s => s.id === user.segment) || SEGMENTS[0];
  const healthTags = parseHealthTags(user.healthConditions);

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100]"
        style={{ background: "rgba(15,15,15,0.25)", backdropFilter: "blur(4px)" }}
        onClick={onClose} />

      <motion.div initial={{ x: 380, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
        exit={{ x: 380, opacity: 0 }} transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
        className="ad-drawer fixed right-0 top-0 bottom-0 overflow-y-auto z-[101]"
        style={{
          width: 440,
          background: "rgba(255,253,248,0.98)",
          borderLeft: `1px solid rgba(239,229,216,0.8)`,
          boxShadow: "-24px 0 80px rgba(0,0,0,0.12)",
          scrollbarWidth: "none"
        }}>

        {/* Drawer Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b bg-white/90 backdrop-blur-md"
          style={{ borderColor: "rgba(239,229,216,0.6)" }}>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: seg.color }} />
            <p className="text-[13px] font-black text-zinc-900 uppercase tracking-wider">User Priority Profile</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 hover:text-zinc-900 border-none cursor-pointer">
            <FiX size={15} />
          </button>
        </div>

        {/* Hero Card */}
        <div className="px-6 py-5 border-b relative overflow-hidden" style={{ borderColor: "rgba(239,229,216,0.6)" }}>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-[18px] font-black shrink-0 shadow-md overflow-hidden"
              style={{ background: user.grad || AVGRADS[0], fontFamily: "'Poppins',sans-serif" }}>
              {user.profileImageUrl && !imgError ? (
                <img
                  src={user.profileImageUrl}
                  alt={user.name}
                  onError={() => setImgError(true)}
                  className="w-full h-full object-cover"
                />
              ) : (
                user.initials
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h2 className="font-black text-[17px] text-zinc-900 tracking-tight" style={{ fontFamily: "'Poppins',sans-serif" }}>
                  {user.name}
                </h2>
                <TierPill tier={user.tier} />
              </div>
              <p className="text-[12px] text-zinc-500 truncate mb-2">{user.email}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full"
                  style={{ background: `${seg.color}15`, color: seg.color, border: `1px solid ${seg.color}30` }}>
                  {seg.label}
                </span>
                <span className="text-[11px] text-zinc-400">Last active: {user.lastSeen || "Recently"}</span>
              </div>
            </div>
            <PriorityBadge score={user.priorityScore} />
          </div>
        </div>

        {/* Health & Yoga Profile Section */}
        <div className="px-6 py-4 border-b bg-purple-50/30" style={{ borderColor: "rgba(239,229,216,0.6)" }}>
          <p className="text-[10px] font-black uppercase tracking-wider text-purple-900 mb-2 flex items-center gap-1.5">
            <FiHeart size={12} className="text-purple-600" /> Health & Yoga Preferences
          </p>
          {healthTags.length > 0 ? (
            <div className="flex items-center gap-1.5 flex-wrap mb-2">
              {healthTags.map((h, i) => (
                <span key={i} className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-purple-100/80 text-purple-800 border border-purple-200">
                  ⚠️ {h}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-zinc-400 mb-2 font-medium">No active health restrictions logged.</p>
          )}
          {user.healthNotes && (
            <div className="p-3 rounded-xl bg-white border border-purple-100 text-[12px] text-zinc-700 leading-relaxed mb-2 shadow-sm">
              <strong className="text-purple-950 font-bold block mb-0.5">Instructor / Medical Notes:</strong>
              {user.healthNotes}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 text-[11px] text-zinc-600 bg-white/80 p-2.5 rounded-xl border border-purple-100/60 shadow-sm">
            <div>
              <span className="text-zinc-400 block text-[9px] uppercase font-bold">Yoga Goal</span>
              <span className="font-extrabold text-zinc-800">{user.yogaGoal || "General Wellness"}</span>
            </div>
            <div>
              <span className="text-zinc-400 block text-[9px] uppercase font-bold">Experience Level</span>
              <span className="font-extrabold text-zinc-800">{user.yogaLevel || "Beginner"}</span>
            </div>
            {user.yogaStyle && (
              <div>
                <span className="text-zinc-400 block text-[9px] uppercase font-bold">Yoga Style</span>
                <span className="font-extrabold text-zinc-800">{user.yogaStyle}</span>
              </div>
            )}
            {user.dietaryPreference && (
              <div>
                <span className="text-zinc-400 block text-[9px] uppercase font-bold">Diet</span>
                <span className="font-extrabold text-zinc-800">{user.dietaryPreference}</span>
              </div>
            )}
          </div>
        </div>

        {/* Account Metadata Grid */}
        <div className="px-6 py-4 border-b space-y-2" style={{ borderColor: "rgba(239,229,216,0.6)" }}>
          <p className="text-[10px] font-black uppercase tracking-wider text-zinc-400 mb-2">Account Details</p>
          {[
            ["Membership Tier", user.tier || "Standard"],
            ["Priority Urgency", user.priorityScore != null ? `${user.priorityScore}/100` : "—"],
            ["Alert Category", seg.label],
            ...(user.daysLeft != null && user.daysLeft > 0 ? [["Days Remaining", `${user.daysLeft} days`]] : []),
            ...(user.inactiveDays != null ? [["Inactive Duration", `${user.inactiveDays} days`]] : []),
            ...(user.phone ? [["Phone Number", user.phone]] : []),
            ...(user.amount ? [["Amount Paid", `₹${user.amount.toLocaleString()}`]] : []),
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between items-center py-1.5 border-b border-zinc-100 text-[12px]">
              <span className="text-zinc-500 font-medium">{k}</span>
              <span className="font-extrabold text-zinc-900">{v}</span>
            </div>
          ))}
        </div>

        {/* Quick Admin Actions */}
        <div className="px-6 py-5 space-y-2.5">
          <p className="text-[10px] font-black uppercase tracking-wider text-zinc-400 mb-2">Direct Actions</p>
          
          {seg.id !== "health" && (
            <motion.button whileHover={{ y: -1 }}
              onClick={() => { onAction(user, seg); onClose(); }}
              className="w-full h-11 rounded-full text-white font-extrabold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer shadow-md"
              style={{ background: `linear-gradient(135deg, ${seg.color}, ${seg.color}DD)` }}>
              <seg.actionIcon size={14} /> Execute {seg.action}
            </motion.button>
          )}

          <motion.button whileHover={{ y: -1 }}
            onClick={() => { onNotify(user, seg); onClose(); }}
            className="w-full h-11 rounded-full text-zinc-800 font-extrabold text-[13px] flex items-center justify-center gap-2 border border-zinc-200 bg-white cursor-pointer hover:bg-zinc-50 shadow-sm">
            <FiBell size={14} className="text-amber-500" /> Send Targeted Notification
          </motion.button>
        </div>
      </motion.div>
    </>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function HighPriorityUsersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "all";

  const [search, setSearch] = useState("");
  const [segFilter, setSegFilter] = useState(initialTab);
  const [drawerUser, setDrawerUser] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modals state
  const [extendUser, setExtendUser] = useState(null);
  const [notifyTarget, setNotifyTarget] = useState(null);

  const [realData, setRealData] = useState({
    expiring: [],
    inactive: [],
    failed: [],
    completion: [],
    new: [],
    health: []
  });

  const [counts, setCounts] = useState({
    Expiring: 0,
    Inactive: 0,
    Failed: 0,
    Completion: 0,
    New: 0,
    Health: 0,
    Total: 0
  });

  const fetchPriorityData = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getPriorityAlerts();
      if (res.success && res.data) {
        if (res.data.usersBySegment || res.data.UsersBySegment) {
          const ubs = res.data.usersBySegment || res.data.UsersBySegment;
          setRealData({
            expiring: (ubs.expiring || ubs.Expiring || []).map((u, i) => ({
              ...u,
              segment: "expiring",
              profileImageUrl: u.profileImageUrl || u.ProfileImageUrl,
              initials: (u.name || u.Name || "U").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase(),
              grad: AVGRADS[i % AVGRADS.length],
              urgencyColor: (u.daysLeft ?? u.DaysLeft ?? 7) <= 3 ? T.red : T.amber,
              lastSeen: u.lastSeen || u.LastSeen || "Active recently",
              label: u.label || u.Label || `${u.daysLeft ?? 7}d left`
            })),
            inactive: (ubs.inactive || ubs.Inactive || []).map((u, i) => ({
              ...u,
              segment: "inactive",
              profileImageUrl: u.profileImageUrl || u.ProfileImageUrl,
              initials: (u.name || u.Name || "U").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase(),
              grad: AVGRADS[(i + 1) % AVGRADS.length],
              urgencyColor: T.amber,
              lastSeen: u.lastSeen || u.LastSeen || "Not available",
              label: u.label || u.Label || (u.inactiveDays != null ? `${u.inactiveDays}d inactive` : "Inactive")
            })),
            failed: (ubs.failed || ubs.Failed || []).map((u, i) => ({
              ...u,
              segment: "failed",
              profileImageUrl: u.profileImageUrl || u.ProfileImageUrl,
              initials: (u.name || u.Name || "U").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase(),
              grad: AVGRADS[(i + 2) % AVGRADS.length],
              urgencyColor: T.red,
              lastSeen: u.lastSeen || u.LastSeen || "Payment pending",
              label: u.label || u.Label || "Cash Pending"
            })),
            completion: (ubs.completion || ubs.Completion || []).map((u, i) => ({
              ...u,
              segment: "completion",
              profileImageUrl: u.profileImageUrl || u.ProfileImageUrl,
              initials: (u.name || u.Name || "U").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase(),
              grad: AVGRADS[(i + 3) % AVGRADS.length],
              urgencyColor: T.olive,
              lastSeen: u.lastSeen || u.LastSeen || (u.daysLeft ? `${u.daysLeft}d left in plan` : "Active in program"),
              label: u.label || u.Label || (u.progress != null ? `${u.progress}% done` : "Near completion")
            })),
            new: (ubs.new || ubs.New || []).map((u, i) => ({
              ...u,
              segment: "new",
              profileImageUrl: u.profileImageUrl || u.ProfileImageUrl,
              initials: (u.name || u.Name || "U").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase(),
              grad: AVGRADS[(i + 4) % AVGRADS.length],
              urgencyColor: T.blue,
              lastSeen: u.lastSeen || u.LastSeen || "Joined recently",
              label: u.label || u.Label || "New User"
            })),
            health: (ubs.health || ubs.Health || []).map((u, i) => ({
              ...u,
              segment: "health",
              profileImageUrl: u.profileImageUrl || u.ProfileImageUrl,
              initials: (u.name || u.Name || "U").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase(),
              grad: AVGRADS[(i + 5) % AVGRADS.length],
              urgencyColor: T.purple,
              lastSeen: u.lastSeen || u.LastSeen || "Instructor review",
              label: u.label || u.Label || "Health Flag"
            }))
          });
        }
        if (res.data.counts || res.data.Counts) {
          const c = res.data.counts || res.data.Counts;
          setCounts({
            Expiring: c.expiring ?? c.Expiring ?? 0,
            Inactive: c.inactive ?? c.Inactive ?? 0,
            Failed: c.failed ?? c.Failed ?? 0,
            Completion: c.completion ?? c.Completion ?? 0,
            New: c.new ?? c.New ?? 0,
            Health: c.health ?? c.Health ?? 0,
            Total: c.total ?? c.Total ?? 0
          });
        }
      }
    } catch (err) {
      console.error("Failed to load priority alerts", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPriorityData();
  }, []);

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Action dispatcher
  const handleAction = async (user, seg) => {
    if (seg.id === "expiring") {
      setExtendUser(user);
    } else if (seg.id === "inactive" || seg.id === "new") {
      setNotifyTarget({ type: "user", user, seg });
    } else if (seg.id === "failed") {
      // Activate cash or manual approve
      try {
        await adminApi.extendSubscription({
          subscriptionId: user.subscriptionId && user.subscriptionId > 0 ? Number(user.subscriptionId) : null,
          userId: Number(user.id),
          days: 30
        });
        showToast(`✓ Cash payment confirmed & activated for ${user.name}!`);
        fetchPriorityData();
      } catch (e) {
        showToast(e.message || "Failed to activate cash payment", "error");
      }
    } else if (seg.id === "completion") {
      navigate(`/admin/subscriptions?tab=all&search=${encodeURIComponent(user.email || user.name)}`);
    } else if (seg.id === "health") {
      setDrawerUser(user);
    }
  };

  const handleNotifyUser = (user, seg) => {
    setNotifyTarget({ type: "user", user, seg });
  };

  const handleNotifyAllSegment = (seg) => {
    setNotifyTarget({ type: "segment", seg });
  };

  // CSV Export
  const handleExportCSV = () => {
    const all = Object.values(realData).flat();
    if (all.length === 0) {
      showToast("No data to export", "error");
      return;
    }
    const headers = ["ID", "Name", "Email", "Phone", "Segment", "Tier", "PriorityScore", "StatusLabel", "LastSeen"];
    const rows = all.map(u => [
      u.id,
      `"${(u.name || "").replace(/"/g, '""')}"`,
      `"${u.email || ""}"`,
      `"${u.phone || ""}"`,
      u.segment,
      u.tier,
      u.priorityScore,
      `"${u.label || ""}"`,
      `"${u.lastSeen || ""}"`
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `DivyaYoga_Priority_Users_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("✓ Priority users report exported successfully!");
  };

  const allUsers = useMemo(() => Object.values(realData).flat(), [realData]);
  const totalPriority = counts.Total || allUsers.length;
  const criticalCount = counts.Expiring + counts.Failed;

  const visibleSegments = useMemo(() => {
    if (segFilter !== "all") return SEGMENTS.filter(s => s.id === segFilter);
    if (!search.trim()) return SEGMENTS;
    return SEGMENTS.filter(s => {
      const users = realData[s.id] || [];
      return users.some(u =>
        (u.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (u.email || "").toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [segFilter, search, realData]);

  return (
    <div style={{ fontFamily: "'Inter',sans-serif", background: "#FFF8EE", minHeight: "100vh" }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@700;800;900&family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      <AdminDashboardLayout activeNav="priority">
        {/* Ambient background orbs */}
        <motion.div animate={{ y: [0, -18, 0], x: [0, 14, 0] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          className="fixed w-[460px] h-[460px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle,rgba(226,75,74,0.06),transparent 70%)", filter: "blur(80px)", top: -140, right: -80, zIndex: 0 }} />
        <motion.div animate={{ y: [0, 14, 0], x: [0, -10, 0] }} transition={{ duration: 17, repeat: Infinity, ease: "easeInOut" }}
          className="fixed w-[340px] h-[340px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle,rgba(245,158,11,0.06),transparent 70%)", filter: "blur(80px)", bottom: -80, left: 40, zIndex: 0 }} />

        <div className="relative z-10 w-full min-w-0 py-2">

          {/* Top Bar Header */}
          <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}
            className="ad-toolbar flex items-center justify-between mb-6 flex-wrap gap-4">
            <div>
              <h1 className="font-black text-[#1F1F1F]"
                style={{ fontFamily: "'Poppins',sans-serif", fontSize: 26, letterSpacing: "-1px" }}>
                High Priority Users
              </h1>
              <p className="text-[12px] mt-1" style={{ color: T.muted }}>
                {totalPriority} users need attention · <strong className="text-red-600 font-black">{criticalCount} critical alerts</strong> · live
              </p>
            </div>

            <div className="ad-wrap flex gap-2.5">
              <motion.button whileHover={{ y: -1 }} onClick={handleExportCSV}
                className="h-10 px-4 rounded-full text-[12px] font-bold flex items-center gap-2 bg-white border border-zinc-200 text-zinc-700 cursor-pointer shadow-sm">
                <FiDownload size={14} /> Export CSV Report
              </motion.button>
              
              <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                onClick={() => setNotifyTarget({ type: "segment", seg: SEGMENTS[0] })}
                className="h-10 px-5 rounded-full text-white font-extrabold text-[12px] flex items-center gap-2 border-none cursor-pointer shadow-md"
                style={{ background: `linear-gradient(135deg, ${T.orange}, ${T.orange2})` }}>
                <FiBell size={14} /> Broadcast Urgent Alert
              </motion.button>
            </div>
          </motion.div>

          {/* KPI Summary Cards */}
          <div className="ad-kpi grid grid-cols-6 gap-3 mb-6">
            {[
              { label: "Expiring (7d)", count: counts.Expiring, color: T.red, id: "expiring", icon: FiCalendar, note: "Needs renewal" },
              { label: "Inactive 14d+", count: counts.Inactive, color: T.amber, id: "inactive", icon: FiUserX, note: "Re-engage" },
              { label: "Failed Cash", count: counts.Failed, color: T.red, id: "failed", icon: FiCreditCard, note: "Action req." },
              { label: "Near End", count: counts.Completion, color: T.olive, id: "completion", icon: FiAward, note: "Upgrade path" },
              { label: "New This Month", count: counts.New, color: T.blue, id: "new", icon: FiUserCheck, note: "Onboarding" },
              { label: "Health Flag", count: counts.Health, color: T.purple, id: "health", icon: FiHeart, note: "Review notes" },
            ].map(k => {
              const isSelected = segFilter === k.id;
              const Icon = k.icon;
              return (
                <motion.div key={k.label} whileHover={{ y: -4, boxShadow: "0 18px 36px -10px rgba(0,0,0,0.1)" }}
                  onClick={() => setSegFilter(segFilter === k.id ? "all" : k.id)}
                  className={`ad-kpi-card group relative overflow-hidden rounded-[22px] p-4 border cursor-pointer transition-all duration-300 flex flex-col justify-between ${
                    isSelected ? "ring-2 ring-offset-1 shadow-lg" : ""
                  }`}
                  style={{
                    background: isSelected ? "#fff" : "rgba(255,255,255,0.88)",
                    borderColor: isSelected ? k.color : "rgba(239,229,216,0.7)",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    boxShadow: isSelected ? `0 12px 30px -6px ${k.color}35` : "0 8px 24px -4px rgba(0,0,0,0.03)",
                  }}>
                  {/* Subtle top spotlight */}
                  <div className="ad-kpi-orb absolute -top-8 -right-8 w-20 h-20 rounded-full opacity-20 group-hover:opacity-45 transition-opacity duration-500 pointer-events-none"
                    style={{ background: k.color, filter: "blur(20px)" }} />

                  <div className="ad-kpi-body">
                    {/* Icon + Mini indicator */}
                    <div className="ad-kpi-iconrow flex items-center justify-between mb-2.5">
                      <div className="ad-kpi-icon w-8 h-8 rounded-[11px] flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-105"
                        style={{ background: `${k.color}15`, color: k.color, border: `1px solid ${k.color}25` }}>
                        <Icon size={14} />
                      </div>
                      <span className="ad-kpi-note w-2 h-2 rounded-full" style={{ background: k.color, boxShadow: `0 0 8px ${k.color}80` }} />
                    </div>

                    {/* Label */}
                    <p className="ad-kpi-lbl text-[10px] font-black uppercase tracking-[0.8px] text-zinc-400 mb-0.5 truncate"
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      {k.label}
                    </p>

                    {/* Bold Count */}
                    <p className="ad-kpi-val font-black text-[26px] leading-none mb-2"
                      style={{ fontFamily: "'Poppins',sans-serif", color: k.color, letterSpacing: "-1px" }}>
                      {loading ? "…" : k.count}
                    </p>
                  </div>

                  {/* Micro note pill */}
                  <div className="ad-kpi-note flex items-center justify-between pt-2 border-t" style={{ borderColor: "rgba(239,229,216,0.5)" }}>
                    <span className="text-[9.5px] font-extrabold text-zinc-500 truncate">{k.note}</span>
                    <span className="text-[9px] font-black" style={{ color: k.color }}>
                      {isSelected ? "ACTIVE" : "VIEW →"}
                    </span>
                  </div>

                  {/* Bottom accent glow bar */}
                  <div className="absolute bottom-0 left-0 right-0 h-[2.5px] opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ background: k.color }} />
                </motion.div>
              );
            })}
          </div>

          {/* Search + Filter Strip */}
          <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
            {/* Filter Tabs */}
            <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-white/80 border border-zinc-200 overflow-x-auto max-w-full">
              {[{ id: "all", label: "All Priority Users" }, ...SEGMENTS].map(s => (
                <button key={s.id} onClick={() => setSegFilter(s.id)}
                  className={`px-3.5 py-1.5 rounded-xl text-[12px] font-extrabold whitespace-nowrap transition-all border-none cursor-pointer ${
                    segFilter === s.id ? "bg-zinc-900 text-white shadow-sm" : "text-zinc-600 hover:text-zinc-900 bg-transparent"
                  }`}>
                  {s.label}
                </button>
              ))}
            </div>

            {/* Live Search */}
            <div className="ad-full relative w-72">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" size={15} />
              <input
                type="text"
                maxLength={100}
                value={search}
                onChange={e => setSearch(e.target.value.replace(/[<>{}]/g, ""))}
                placeholder="Search priority user..."
                className="w-full pl-9 pr-8 py-2 rounded-full text-[12px] font-bold border border-zinc-200 bg-white/90 outline-none focus:border-amber-500 shadow-sm transition-all"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 border-none bg-transparent cursor-pointer">
                  <FiX size={13} />
                </button>
              )}
            </div>
          </div>

          {/* 6 Real-time Priority Accordions */}
          {loading ? (
            <div className="rounded-[24px] p-8 border bg-white/80" style={{ borderColor: "rgba(255,255,255,0.95)" }}>
              <BrandLoader message="Loading priority user alerts..." minHeight="min-h-[280px]" />
            </div>
          ) : (
            <div className="space-y-3">
              {visibleSegments.map(seg => (
                <SegmentPanel key={seg.id} seg={seg}
                  users={realData[seg.id] || []}
                  onView={user => setDrawerUser(user)}
                  onAction={handleAction}
                  onNotify={handleNotifyUser}
                  onNotifyAll={handleNotifyAllSegment}
                  defaultOpen={segFilter === seg.id || (segFilter === "all" && seg.urgency === "critical")} />
              ))}
            </div>
          )}

        </div>

        {/* User Profile Detail Drawer */}
        <AnimatePresence>
          {drawerUser && (
            <UserDrawer user={drawerUser}
              onClose={() => setDrawerUser(null)}
              onAction={handleAction}
              onNotify={handleNotifyUser}
              showToast={showToast} />
          )}
        </AnimatePresence>

        {/* Extension Modal */}
        <AnimatePresence>
          {extendUser && (
            <ExtendModal user={extendUser}
              onClose={() => setExtendUser(null)}
              onSuccess={fetchPriorityData}
              showToast={showToast} />
          )}
        </AnimatePresence>

        {/* Targeted Notification Modal */}
        <AnimatePresence>
          {notifyTarget && (
            <SendNotificationModal target={notifyTarget}
              onClose={() => setNotifyTarget(null)}
              showToast={showToast} />
          )}
        </AnimatePresence>

        {/* Dynamic Toast Alert */}
        <AnimatePresence>
          {toast && (
            <motion.div initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="ad-toast fixed bottom-6 right-6 z-[300] px-5 py-3 rounded-2xl text-white font-black text-[13px] shadow-2xl flex items-center gap-2"
              style={{ background: toast.type === "error" ? T.red : "#1F1F1F" }}>
              <span>{toast.msg}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </AdminDashboardLayout>
    </div>
  );
}
